/**
 * Retrocompatibilidade dos bancos IndexedDB renomeados para a família `BF_SELLER_*`.
 *
 * O que esta migração resolve: o aparelho que atualiza de uma versão anterior continua com todos os
 * dados no disco, sob os nomes antigos, e o app novo abre outros nomes — encontra tudo vazio. Sem
 * isso o RCA perde o carrinho que estava montando, perde pedido offline ainda não enviado (o único
 * dado do app que não existe em nenhum outro lugar) e rebaixa o catálogo de imagens pela rede móvel.
 *
 * São duas fases, por custo:
 *
 *  - **crítica** (`migrateLegacyIdbCritical`): copia carrinhos e pedidos. Poucos KB, roda antes do
 *    primeiro render — é dado que não pode existir uma janela de tempo em que o app não o vê;
 *  - **pesada** (`migrateLegacyIdbHeavy`): apaga os bancos antigos (datasets, e os dois que a fase
 *    crítica acabou de importar), copia as imagens e migra o `download_registers`. Roda com o app
 *    já em uso, em lotes.
 *
 * Nenhuma exclusão acontece no caminho do boot, e isso é deliberado: `deleteDB` de um banco que
 * outra aba mantém aberta não falha, fica *pendente*, e uma aba com a versão anterior do app segura
 * o banco antigo enquanto viver. Copiar primeiro e apagar depois também é a ordem que garante que
 * nada se perde entre as duas coisas.
 *
 * Cada domínio tem seu marcador em `localStorage` e só avança quando o passo conclui: interrupção
 * no meio (app fechado, aba morta) refaz o trabalho no próximo boot.
 */

import type { IDBPDatabase, IDBPTransaction } from 'idb';
import { toCoded } from '@/shared/coded';
import { yieldToMain } from '@/shared/yieldToMain';
import { importLegacyCartRows } from '@/shared/cartStore';
import { importLegacyOrderRows } from '@/shared/ordersStore';
import { openImagesDBConnection } from '@/talons/dbFunctions/useDBFunctions';
import {
  captureLegacyDownloadRegisters,
  clearStashedDownloadRegisters,
  seedImageDatasetMetaFromStash,
} from './migrateDownloadRegisters';
import {
  LEGACY_CARTS_DB,
  LEGACY_CARTS_STORE,
  LEGACY_DATASETS_DB,
  LEGACY_IMAGES_DB,
  LEGACY_IMAGES_STORE,
  LEGACY_ORDERS_DB,
  LEGACY_ORDERS_STORE,
  dropLegacyDb,
  openLegacyDbIfExists,
  readLegacyKeyvalStore,
} from './legacyIdbNames';

const MARCADOR_CARRINHOS = 'bf_seller_legacy_idb_carts';
const MARCADOR_PEDIDOS = 'bf_seller_legacy_idb_orders';
const MARCADOR_DATASETS = 'bf_seller_legacy_idb_datasets';
const MARCADOR_IMAGENS = 'bf_seller_legacy_idb_images';
const MARCADOR_REGISTROS = 'bf_seller_legacy_idb_download_registers';

/** Última chave copiada da store de imagens: é o que permite retomar de onde parou. */
const CURSOR_IMAGENS = 'bf_seller_legacy_idb_images_cursor';

const CONCLUIDO = '1';

/**
 * Importado, mas o banco antigo ainda está no disco.
 *
 * Os dois passos são marcados em separado porque falham por motivos diferentes, e reagir do jeito
 * errado a cada um custa dado: reimportar pode reverter uma edição do RCA (a mesclagem de carrinho
 * prefere quem tem mais itens), enquanto reexcluir é inofensivo.
 */
const IMPORTADO = 'imported';

/** Imagens por transação. O mesmo lote do `abrirSessaoImagens`, pelo mesmo motivo: pico de memória. */
const LOTE_IMAGENS = 200;

/** Espera o boot terminar antes de encostar no disco. Menor que o do `useUnpackImagesZip` (5s). */
const ATRASO_FASE_PESADA_MS = 3000;

const STORE_IMAGENS = toCoded('imagens');

const lerMarcador = (chave: string): string | null => {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
};

const gravarMarcador = (chave: string, valor: string): void => {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    /* storage cheio/bloqueado: refaz no próximo boot */
  }
};

const apagarMarcador = (chave: string): void => {
  try {
    localStorage.removeItem(chave);
  } catch {
    /* idem */
  }
};

const concluido = (chave: string): boolean => lerMarcador(chave) === CONCLUIDO;

const importado = (chave: string): boolean => {
  const valor = lerMarcador(chave);
  return valor === IMPORTADO || valor === CONCLUIDO;
};

/** `true` quando a fase pesada não tem mais nada a fazer — inclui as exclusões que ela herdou. */
export const isLegacyHeavyMigrationDone = (): boolean =>
  [MARCADOR_DATASETS, MARCADOR_IMAGENS, MARCADOR_REGISTROS, MARCADOR_CARRINHOS, MARCADOR_PEDIDOS].every(concluido);

/**
 * Encerra a **fase pesada** sem rodar nada.
 *
 * Para o reset forçado por versão (`maybeApplyForcedCacheReset`), que apaga os bancos de datasets e
 * de imagens legados junto com os novos, de propósito: ressuscitar imagens antigas depois disso
 * desfaria justamente o que o reset foi publicado para fazer. E o reset limpa o `localStorage`, onde
 * ficam estes marcadores — sem regravá-los, a fase pesada copiaria tudo de volta no boot seguinte.
 *
 * Carrinhos e pedidos **não** entram aqui, e é uma distinção com consequência: marcá-los sem ter
 * importado deixaria os dois bancos antigos no disco para sempre, sem ninguém para lê-los. Quem
 * decide sobre eles é só a fase crítica, e só depois de a importação ter voltado sem erro.
 */
export const markLegacyHeavyMigrationDone = (): void => {
  [MARCADOR_DATASETS, MARCADOR_IMAGENS, MARCADOR_REGISTROS].forEach((chave) => gravarMarcador(chave, CONCLUIDO));
  apagarMarcador(CURSOR_IMAGENS);
  // O reset apagou as imagens: semear "este zip já veio" a partir do stash mandaria o app não
  // baixar de novo o que acabou de ser jogado fora.
  clearStashedDownloadRegisters();
};

/**
 * Copia uma store no formato `idb-keyval` para o banco novo. **Não apaga o banco antigo.**
 *
 * A exclusão fica de fora de propósito: `deleteDB` de um banco que outra aba mantém aberta não
 * rejeita, fica *pendente* até aquela conexão fechar — e esta função roda antes do primeiro render.
 * Uma aba com a versão anterior do app segura o `PWA_RCA_CARTS` pelo tempo que ficar viva, e o
 * preço seria tela branca até o RCA fechá-la. Quem apaga é `dropLegacyKeyvalDbs`, na fase pesada,
 * onde esperar não custa nada.
 *
 * Se `importar` lançar, nada é marcado: o banco antigo continua com os dados e a próxima abertura
 * tenta de novo.
 */
const migrarStoreKeyval = async (
  marcador: string,
  dbName: string,
  storeName: string,
  importar: (rows: Record<string, string>) => Promise<number>,
  rotulo: string
): Promise<number> => {
  if (importado(marcador)) return 0;

  try {
    const linhas = await readLegacyKeyvalStore(dbName, storeName);
    const total = Object.keys(linhas).length ? await importar(linhas) : 0;

    gravarMarcador(marcador, IMPORTADO);

    if (total) console.info(`[legacyIdb] ${total} ${rotulo} migrados de ${dbName}`);
    return total;
  } catch (error) {
    console.error(`[legacyIdb] falha ao migrar ${rotulo} de ${dbName}:`, error);
    return 0;
  }
};

/**
 * Apaga os bancos de carrinho e de pedido antigos, já importados.
 *
 * Segunda metade de `migrarStoreKeyval`, adiada para a fase pesada pelo motivo descrito lá. Só
 * apaga o que já foi importado, e só marca `CONCLUIDO` quando a exclusão confirma — bloqueada por
 * outra aba, a tentativa se repete na próxima abertura. Reexcluir não custa dado nenhum, então
 * repetir aqui é seguro de um jeito que reimportar não seria.
 */
export const dropLegacyKeyvalDbs = async (): Promise<void> => {
  for (const [marcador, dbName] of [
    [MARCADOR_CARRINHOS, LEGACY_CARTS_DB],
    [MARCADOR_PEDIDOS, LEGACY_ORDERS_DB],
  ] as const) {
    if (concluido(marcador) || !importado(marcador)) continue;
    if (await dropLegacyDb(dbName)) gravarMarcador(marcador, CONCLUIDO);
  }
};

/**
 * Fase crítica: carrinhos e pedidos offline.
 *
 * Rode **antes do primeiro render** e antes do reset forçado por versão, que limpa o `localStorage`
 * onde ficam os marcadores. São poucos KB — o custo de esperar é irrelevante ao lado de exibir ao
 * RCA um carrinho vazio que ele não esvaziou.
 *
 * Só copia; a limpeza dos bancos antigos é da fase pesada (ver `dropLegacyKeyvalDbs`).
 */
export const migrateLegacyIdbCritical = async (): Promise<{ carrinhos: number; pedidos: number }> => {
  const carrinhos = await migrarStoreKeyval(
    MARCADOR_CARRINHOS,
    LEGACY_CARTS_DB,
    LEGACY_CARTS_STORE,
    importLegacyCartRows,
    'carrinhos'
  );

  const pedidos = await migrarStoreKeyval(
    MARCADOR_PEDIDOS,
    LEGACY_ORDERS_DB,
    LEGACY_ORDERS_STORE,
    importLegacyOrderRows,
    'pedidos'
  );

  return { carrinhos, pedidos };
};

/**
 * Apaga o banco de datasets antigo.
 *
 * Nada é copiado: catálogo, preço, cliente e o resto são dados do servidor, e o sync os reescreve
 * no `BF_SELLER_DATASETS` no formato novo. A store `offline_events` fica para trás junto — é fila
 * de telemetria (`LogEvents`), não dado de negócio.
 *
 * Apagar cedo é o que importa: são centenas de MB, e é essa pressão de disco que faz o navegador
 * descartar o banco novo num aparelho apertado de espaço.
 */
export const dropLegacyDatasetsDb = async (): Promise<boolean> => {
  if (concluido(MARCADOR_DATASETS)) return false;

  const apagado = await dropLegacyDb(LEGACY_DATASETS_DB);
  if (apagado) gravarMarcador(MARCADOR_DATASETS, CONCLUIDO);
  return apagado;
};

const lerCursorImagens = (): IDBValidKey | null => {
  const bruto = lerMarcador(CURSOR_IMAGENS);
  if (bruto == null || bruto === '') return null;
  // A chave é o `COD_PRODUTO` (número); um `localStorage` só guarda texto.
  return /^\d+$/.test(bruto) ? Number(bruto) : bruto;
};

const gravarCursorImagens = (chave: IDBValidKey): void => {
  if (typeof chave !== 'number' && typeof chave !== 'string') return;
  gravarMarcador(CURSOR_IMAGENS, String(chave));
};

const contarImagens = async (db: IDBPDatabase): Promise<number> => {
  try {
    const tx = db.transaction(STORE_IMAGENS, 'readonly');
    const total = await tx.objectStore(STORE_IMAGENS).count();
    await tx.done;
    return total;
  } catch (error) {
    console.info('[legacyIdb] não foi possível contar as imagens do banco novo:', error);
    return 0;
  }
};

/**
 * Grava um lote de imagens legadas na store nova, **sem reconverter o conteúdo**.
 *
 * A linha antiga é `{ COD_PRODUTO, imagem: <base64> }` e a store nova prefere bytes crus — mas
 * `materializarImagem` (no `useDBFunctions`) já lê base64, `Blob` e bytes, e o `buildImageSrc`
 * monta o `src` a partir de qualquer um dos três. Decodificar milhares de imagens aqui só
 * encareceria a migração para um formato que o próximo sync de imagens reescreve de todo jeito.
 */
const gravarLoteImagens = async (db: IDBPDatabase, linhas: unknown[]): Promise<number> => {
  const validas = (linhas as Array<{ COD_PRODUTO?: unknown; imagem?: unknown }>).filter(
    (linha) => linha && Number.isFinite(Number(linha.COD_PRODUTO)) && linha.imagem != null
  );
  if (!validas.length) return 0;

  const tx = db.transaction(STORE_IMAGENS, 'readwrite');
  const store = (tx as IDBPTransaction<unknown, [string], 'readwrite'>).objectStore(STORE_IMAGENS);
  for (const linha of validas) {
    void store.put({ ...linha, COD_PRODUTO: Number(linha.COD_PRODUTO) }).catch(() => {});
  }
  await tx.done;

  return validas.length;
};

/**
 * Copia as imagens do banco antigo para o novo, em lotes, e apaga o antigo ao terminar.
 *
 * Ler a store inteira de uma vez não é opção: são as imagens do catálogo todo, centenas de MB em
 * base64. A varredura é paginada por faixa de chave (`lowerBound` acima da última copiada), e a
 * última chave de cada lote vai para o `localStorage` — fechar o app no meio retoma daqui, não do
 * começo.
 */
export const migrateLegacyImages = async (): Promise<number> => {
  if (concluido(MARCADOR_IMAGENS)) return 0;

  const legado = await openLegacyDbIfExists(LEGACY_IMAGES_DB);
  if (!legado) {
    gravarMarcador(MARCADOR_IMAGENS, CONCLUIDO);
    return 0;
  }

  if (!legado.objectStoreNames.contains(LEGACY_IMAGES_STORE)) {
    legado.close();
    await dropLegacyDb(LEGACY_IMAGES_DB);
    gravarMarcador(MARCADOR_IMAGENS, CONCLUIDO);
    return 0;
  }

  let novo: IDBPDatabase | undefined;
  let copiadas = 0;
  let terminou = false;

  try {
    novo = await openImagesDBConnection();

    let ultimaChave = lerCursorImagens();

    // Aparelho que já sincronizou no formato novo não tem o que ganhar com as imagens antigas, e
    // copiar centenas de MB por cima delas seria só desgaste de disco. A checagem vale apenas
    // antes do primeiro lote: no meio da migração, a store nova cheia é o próprio progresso.
    if (ultimaChave == null && (await contarImagens(novo)) > 0) {
      terminou = true;
      return 0;
    }

    for (;;) {
      const faixa = ultimaChave == null ? undefined : IDBKeyRange.lowerBound(ultimaChave, true);

      const txLegado = legado.transaction(LEGACY_IMAGES_STORE, 'readonly');
      const storeLegado = txLegado.objectStore(LEGACY_IMAGES_STORE);
      // Chaves e linhas na mesma transação: a chave real vem do banco, não de um campo da linha.
      const [chaves, linhas] = await Promise.all([
        storeLegado.getAllKeys(faixa, LOTE_IMAGENS),
        storeLegado.getAll(faixa, LOTE_IMAGENS),
      ]);
      await txLegado.done;

      if (!chaves.length) break;

      copiadas += await gravarLoteImagens(novo, linhas);

      ultimaChave = chaves[chaves.length - 1];
      gravarCursorImagens(ultimaChave);

      await yieldToMain();
    }

    terminou = true;
    if (copiadas) console.info(`[legacyIdb] ${copiadas} imagens migradas de ${LEGACY_IMAGES_DB}`);
    return copiadas;
  } catch (error) {
    console.error('[legacyIdb] falha ao migrar as imagens legadas:', error);
    return copiadas;
  } finally {
    legado.close();
    novo?.close();

    if (terminou) {
      await dropLegacyDb(LEGACY_IMAGES_DB);
      apagarMarcador(CURSOR_IMAGENS);
      gravarMarcador(MARCADOR_IMAGENS, CONCLUIDO);
    }
  }
};

/**
 * Fase pesada: libera o disco dos datasets antigos, apaga os bancos de carrinho e pedido já
 * importados e por último copia as imagens.
 *
 * A ordem é por espaço livre: as duas exclusões vêm antes da cópia que precisa de espaço.
 */
/**
 * Captura o `download_registers` antes de o banco que o contém ser apagado.
 *
 * É a primeira coisa da fase pesada, e tem de ser: a tabela mora no `BATE_FORTE`, que sai na linha
 * seguinte. Falhar aqui não interrompe o resto — o preço de ficar sem o registro é o zip de imagens
 * descer outra vez, caro mas não destrutivo.
 */
const capturarRegistrosDeDownload = async (): Promise<void> => {
  if (importado(MARCADOR_REGISTROS)) return;

  try {
    const total = await captureLegacyDownloadRegisters();
    gravarMarcador(MARCADOR_REGISTROS, IMPORTADO);
    if (total) console.info(`[legacyIdb] ${total} registro(s) de imagem guardados do download_registers`);
  } catch (error) {
    console.error('[legacyIdb] falha ao capturar o download_registers:', error);
  }
};

/**
 * Semeia o `__meta` com os arquivos de imagem que o registro antigo dá como baixados.
 *
 * Só depois de a cópia das imagens ter **concluído**: registrar "já baixado" no meio dela diria a
 * verdade sobre metade do catálogo, e a outra metade nunca desceria. Enquanto a cópia não termina,
 * o stash espera — é justamente para atravessar esses boots que ele existe.
 */
const semearRegistrosDeDownload = async (): Promise<number> => {
  if (concluido(MARCADOR_REGISTROS) || !importado(MARCADOR_REGISTROS)) return 0;
  if (!concluido(MARCADOR_IMAGENS)) return 0;

  try {
    const semeados = await seedImageDatasetMetaFromStash();
    gravarMarcador(MARCADOR_REGISTROS, CONCLUIDO);
    return semeados;
  } catch (error) {
    console.error('[legacyIdb] falha ao registrar as imagens migradas no __meta:', error);
    return 0;
  }
};

/**
 * Fase pesada: guarda o `download_registers`, libera o disco dos bancos antigos, copia as imagens e
 * registra no `__meta` que elas já vieram.
 *
 * A ordem tem três amarras: a captura precede a exclusão do banco que contém a tabela; as duas
 * exclusões precedem a cópia, que precisa do espaço; e a semeadura sucede a cópia, porque é ela que
 * a torna verdadeira.
 */
export const migrateLegacyIdbHeavy = async (): Promise<{ imagens: number; registros: number }> => {
  await capturarRegistrosDeDownload();

  await dropLegacyDatasetsDb();
  await dropLegacyKeyvalDbs();
  const imagens = await migrateLegacyImages();

  const registros = await semearRegistrosDeDownload();
  return { imagens, registros };
};

/**
 * Agenda a fase pesada para depois do boot.
 *
 * Não é `await`-ável de propósito: quem chama é o `index.tsx`, e nada na tela depende do resultado.
 * O atraso é menor que o do `useUnpackImagesZip` (5s) para que a cópia comece antes da
 * descompactação — as duas escrevem na mesma store, e um aparelho vindo da versão antiga não tem
 * zip nenhum para descompactar.
 */
export const scheduleLegacyIdbHeavyMigration = (atrasoMs: number = ATRASO_FASE_PESADA_MS): void => {
  if (isLegacyHeavyMigrationDone()) return;

  setTimeout(() => {
    void migrateLegacyIdbHeavy().catch((error) => {
      console.error('[legacyIdb] fase pesada da migração falhou:', error);
    });
  }, atrasoMs);
};
